import { type Tweet as ContractsTweet, TweetSchema as ContractsTweetSchema } from '@sweetistics/contracts';
import type { CanonicalTweet } from '@sweetistics/contracts/tweets-canonical';
import { CanonicalTweetSchema } from '@sweetistics/contracts/tweets-canonical';
import { ZodError, z } from 'zod';
import type { ReferencedTweet, TweetAuthor, TweetEntities } from '../../types/tweet';
import { twitterLogger } from '../logger';
import { ensureDate } from '../utils/date-coercion';
import { TweetFromApiSchema } from '../validation/api/tweets';

type NormalizationContext = Record<string, unknown>;

interface NormalizationErrorDetails {
  context?: NormalizationContext;
  error: unknown;
}

export interface NormalizeTweetOptions {
  /** Structured metadata (tweetId, source, etc.) attached to any emitted logs. */
  context?: NormalizationContext;
  /**
   * Optional hook for custom error handling. When provided, the callback is invoked
   * instead of the default logger. Re-throwing is still handled by the caller.
   */
  onError?: (details: NormalizationErrorDetails) => void;
  /**
   * Skip logging when set to true. Useful for high-volume best-effort paths where
   * the caller handles reporting manually.
   */
  suppressLogging?: boolean;
}

export type NormalizeTweetResult = { ok: true; tweet: CanonicalTweet } | { ok: false; error: unknown };

const MAX_LOGGED_ISSUES = 5;

const ZodIssueLikeSchema = z.object({
  path: z.array(z.union([z.string(), z.number(), z.symbol()])),
  code: z.string(),
  message: z.string(),
});

type ZodIssueLike = z.infer<typeof ZodIssueLikeSchema>;

const extractZodIssues = (error: unknown): ZodIssueLike[] | undefined => {
  if (error instanceof ZodError) {
    return error.issues;
  }
  if (typeof error !== 'object' || error === null) {
    return;
  }
  const { issues } = error as { issues?: unknown };
  if (!Array.isArray(issues)) {
    return;
  }
  const filtered = issues
    .map((issue) => {
      const parsed = ZodIssueLikeSchema.safeParse(issue);
      return parsed.success ? parsed.data : null;
    })
    .filter((issue): issue is ZodIssueLike => issue !== null);
  return filtered.length > 0 ? filtered : undefined;
};

const formatZodIssues = (error: unknown): Array<{ path: string; code: string; message: string }> | undefined => {
  const issueList = extractZodIssues(error);
  if (!issueList || issueList.length === 0) {
    return;
  }

  return issueList.slice(0, MAX_LOGGED_ISSUES).map((issue) => ({
    path: issue.path.length > 0 ? issue.path.join('.') : '<root>',
    code: issue.code,
    message: issue.message,
  }));
};

const convertContractAuthor = (author: ContractsTweet['author']): TweetAuthor => {
  const verifiedType = author.verifiedType ?? 'none';
  const base: TweetAuthor = {
    id: author.id,
    username: author.username,
    name: author.name,
    profileImageUrl: author.profileImageUrl ?? undefined,
    verifiedType,
    verified: author.verified ?? (verifiedType === 'none' ? undefined : true),
    createdAt: new Date(0),
  };
  if (author.verified !== undefined) {
    base.verified = author.verified;
  }
  return base;
};

const convertContractEntities = (entities: ContractsTweet['entities'] | undefined): TweetEntities | undefined => {
  if (entities == null) {
    return;
  }

  const next: TweetEntities = {};

  if (Array.isArray(entities.urls) && entities.urls.length > 0) {
    next.urls = entities.urls.map((entry) => ({
      url: entry.url,
      expandedUrl: entry.expandedUrl,
      displayUrl: entry.displayUrl,
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      description: 'description' in entry ? ((entry as { description?: string | null }).description ?? null) : null,
    }));
  }

  if (Array.isArray(entities.hashtags) && entities.hashtags.length > 0) {
    next.hashtags = entities.hashtags.map((entry) => ({
      tag: entry.tag,
    }));
  }

  if (Array.isArray(entities.mentions) && entities.mentions.length > 0) {
    next.mentions = entities.mentions.map((entry) => ({
      username: entry.username,
      id: entry.id,
    }));
  }

  return Object.keys(next).length > 0 ? next : undefined;
};

const convertContractReferencedTweets = (
  referenced: ContractsTweet['referencedTweets'] | undefined
): ReferencedTweet[] | undefined => {
  if (!Array.isArray(referenced) || referenced.length === 0) {
    return;
  }
  return referenced.map((entry) => ({
    id: entry.id,
    type: entry.type === 'replied_to' ? 'repliedTo' : entry.type,
  }));
};

const convertContractTweetToCanonical = (tweet: ContractsTweet): CanonicalTweet => {
  const authorId = tweet.author?.id;
  if (!authorId) {
    throw new Error('Tweet payload missing author id');
  }

  const metrics = tweet.metrics;
  const referencedTweets = convertContractReferencedTweets(tweet.referencedTweets);
  const resolvedMetrics = {
    likeCount: metrics?.likeCount ?? 0,
    retweetCount: metrics?.retweetCount ?? 0,
    replyCount: metrics?.replyCount ?? 0,
    quoteCount: metrics?.quoteCount ?? 0,
    bookmarkCount: metrics?.bookmarkCount ?? undefined,
    impressionCount: metrics?.impressionCount ?? undefined,
  };
  const canonicalTweet: CanonicalTweet = {
    id: tweet.id,
    text: tweet.text,
    authorId,
    createdAt: ensureDate(tweet.createdAt),
    metrics: resolvedMetrics,
    likeCount: resolvedMetrics.likeCount,
    retweetCount: resolvedMetrics.retweetCount,
    replyCount: resolvedMetrics.replyCount,
    quoteCount: resolvedMetrics.quoteCount,
    bookmarkCount: resolvedMetrics.bookmarkCount ?? undefined,
    impressionCount: resolvedMetrics.impressionCount ?? undefined,
    author: convertContractAuthor(tweet.author),
    entities: convertContractEntities(tweet.entities),
    referencedTweets,
    isRetweet: referencedTweets?.some((entry) => entry.type === 'retweeted') ?? undefined,
    isQuote: referencedTweets?.some((entry) => entry.type === 'quoted') ?? undefined,
  };

  return CanonicalTweetSchema.parse(canonicalTweet);
};
const reportNormalizationFailure = (details: NormalizationErrorDetails, options: NormalizeTweetOptions) => {
  if (options.onError) {
    options.onError(details);
    return;
  }
  if (options.suppressLogging) {
    return;
  }

  const { error, context } = details;
  const logger = context ? twitterLogger.with({ scope: 'tweet-normalization', ...context }) : twitterLogger;
  if (error instanceof Error) {
    const issueSummary = formatZodIssues(error);
    logger.error('tweet-normalization failed', {
      name: error.name,
      message: error.message,
      issues: issueSummary,
    });
  } else {
    logger.error('tweet-normalization failed (non-error throw)', {
      value: String(error),
    });
  }
};

/** Parse and normalize a raw Twitter API payload into the internal `Tweet` shape. */
export function normalizeTweetPayload(raw: unknown, options: NormalizeTweetOptions = {}): CanonicalTweet {
  try {
    const domainCandidate = CanonicalTweetSchema.safeParse(raw);
    if (domainCandidate.success) {
      return domainCandidate.data;
    }

    const contractCandidate = ContractsTweetSchema.safeParse(raw);
    if (contractCandidate.success) {
      return convertContractTweetToCanonical(contractCandidate.data);
    }

    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    const schema = TweetFromApiSchema as z.ZodType<CanonicalTweet>;
    return schema.parse(raw);
  } catch (error) {
    const details: NormalizationErrorDetails = { error };
    if (options.context) {
      details.context = options.context;
    }
    reportNormalizationFailure(details, options);
    throw error;
  }
}

/** Best-effort normalization that never throws; callers branch on the `{ ok }` flag. */
export function tryNormalizeTweetPayload(raw: unknown, options: NormalizeTweetOptions = {}): NormalizeTweetResult {
  try {
    const tweet = normalizeTweetPayload(raw, options);
    return { ok: true, tweet };
  } catch (error) {
    return { ok: false, error };
  }
}

export function normalizeTweetMap(
  rawTweets: Record<string, unknown> | null | undefined,
  options: NormalizeTweetOptions = {}
): Record<string, CanonicalTweet> {
  if (!rawTweets) {
    return {};
  }

  const normalized: Record<string, CanonicalTweet> = {};
  for (const [tweetId, rawTweet] of Object.entries(rawTweets)) {
    const mergedContext = options.context ? { ...options.context, tweetId } : { tweetId };
    const result = tryNormalizeTweetPayload(rawTweet, {
      ...options,
      context: mergedContext,
    });
    if (result.ok) {
      normalized[result.tweet.id] = result.tweet;
    }
  }
  return normalized;
}

export function normalizeTweetList(
  rawTweets: unknown[] | null | undefined,
  options: NormalizeTweetOptions = {}
): CanonicalTweet[] {
  if (!rawTweets || rawTweets.length === 0) {
    return [];
  }

  const tweets: CanonicalTweet[] = [];
  for (const [index, candidate] of rawTweets.entries()) {
    const mergedContext = options.context ? { ...options.context, index } : { index };
    const result = tryNormalizeTweetPayload(candidate, {
      ...options,
      context: mergedContext,
    });
    if (result.ok) {
      tweets.push(result.tweet);
    }
  }

  return tweets;
}
